import { FC } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { OrgCountBarChart } from "components/Charts/OrgCountBarChart";
import { useEngagementAnalytics } from "../useEngagementAnalytics";
import {
  getMetricsDateRangeLabel,
  MetricsDateRange,
} from "pages/Metrics/metricsDateRange";
import { MetricsDateRangeFilter } from "pages/Metrics/components/MetricsDateRangeFilter";

type Props = {
  orgIds: string[];
  sinceDate: string;
  dateRange: MetricsDateRange;
  onDateRangeChange: (value: MetricsDateRange) => void;
};

type SummaryCard = {
  title: string;
  value: string;
  helper: string;
};

export const UserEngagementAnalytics: FC<Props> = ({
  orgIds,
  sinceDate,
  dateRange,
  onDateRangeChange,
}) => {
  const { summary, sessionsByOrg, recentSessions, loading, error } =
    useEngagementAnalytics(orgIds, sinceDate);
  const dateRangeLabel = getMetricsDateRangeLabel(dateRange);

  if (orgIds.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        No organizations available for engagement analytics.
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ mt: 2 }}>
        Could not load engagement analytics.
      </Typography>
    );
  }

  const cards: SummaryCard[] = [
    {
      title: "Active users",
      value: `${summary.activeUsers} / ${summary.totalUsers}`,
      helper: `${summary.activeUserRate} of profiles opened a session in the ${dateRangeLabel.toLowerCase()}.`,
    },
    {
      title: "DAU / WAU / MAU",
      value: `${summary.dau} / ${summary.wau} / ${summary.mau}`,
      helper: "Distinct users with a session open today, in 7 days, and in 30 days.",
    },
    {
      title: "Session opens",
      value: `${summary.sessionsInPeriod}`,
      helper: `Authenticated session opens in the ${dateRangeLabel.toLowerCase()}.`,
    },
  ];

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={2}
      >
        <Typography variant="h6">User engagement</Typography>
        <MetricsDateRangeFilter value={dateRange} onChange={onDateRangeChange} />
      </Box>

      <Grid container spacing={2}>
        {cards.map((card) => (
          <Grid item xs={12} md={4} key={card.title}>
            <Card variant="outlined">
              <CardContent sx={{ minHeight: 140 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {card.title}
                </Typography>
                <Typography variant="h5" sx={{ mt: 1 }}>
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {card.helper}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} lg={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active sessions by organization
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Authenticated session opens in the {dateRangeLabel.toLowerCase()}.
              </Typography>
              <OrgCountBarChart
                data={sessionsByOrg}
                valueLabel="Sessions"
                color="#2e7d32"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent active sessions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Most recent authenticated session for each active profile in the{" "}
                {dateRangeLabel.toLowerCase()}.
              </Typography>
              {recentSessions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No active sessions in this period.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Last session</TableCell>
                      <TableCell align="right">Sessions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentSessions.map((row) => (
                      <TableRow key={row.user_id}>
                        <TableCell>
                          {row.user?.name || row.user?.email || row.user_id}
                        </TableCell>
                        <TableCell>{row.user?.role || "-"}</TableCell>
                        <TableCell>
                          {row.last_session_at
                            ? dayjs(row.last_session_at).format("MMM D, YYYY HH:mm")
                            : "-"}
                        </TableCell>
                        <TableCell align="right">{row.session_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
